import { useSyncExternalStore } from 'react'
import { onlineManager } from '@tanstack/react-query'
import { Network } from '@capacitor/network'
import { App as CapacitorApp } from '@capacitor/app'

// Single source of truth for "are we online". Drives the blocking offline
// screen (components/OfflineProvider) — the app has no offline mode.
//
// TanStack Query's onlineManager defaults to window `online`/`offline` events,
// which are unreliable inside WKWebView. Bridge it to @capacitor/network (which
// falls back to navigator.onLine on web). Module-level: runs once, StrictMode-safe.
onlineManager.setEventListener((setOnline) => {
  let removed = false
  let handle: { remove: () => Promise<void> } | undefined

  void Network.getStatus()
    .then((status) => {
      if (!removed) setOnline(status.connected)
    })
    .catch(() => {
      /* keep the default (online) */
    })

  void Network.addListener('networkStatusChange', (status) => {
    if (!removed) setOnline(status.connected)
  }).then((h) => {
    handle = h
    if (removed) void h.remove()
  })

  return () => {
    removed = true
    void handle?.remove()
  }
})

// Recovery. The networkStatusChange event reliably reports a drop but can
// miss the reconnect (notably on the iOS Simulator after the Mac's Wi-Fi
// toggles), which would leave the offline screen up until relaunch. While
// offline, re-read the native status every few seconds and, if that still says
// disconnected, probe the API with a real request: any HTTP response (whatever
// the status code) proves the path works. Also resync on app foreground.
const API_URL = import.meta.env.VITE_API_URL as string | undefined
const RECOVERY_INTERVAL_MS = 5_000
const PROBE_TIMEOUT_MS = 5_000

async function probeApiReachable(): Promise<boolean> {
  if (!API_URL) return false
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  try {
    // Public, unauthenticated RPC procedure; only reachability matters here.
    await fetch(`${API_URL.replace(/\/+$/, '')}/version/check`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
      cache: 'no-store',
      signal: controller.signal,
    })
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Re-check connectivity now: native status first, then a reachability probe
 * when that says disconnected. Used by the recovery loop, app foreground and
 * the offline screen's "Try again" button.
 */
export async function refreshOnlineStatus(): Promise<boolean> {
  let connected = onlineManager.isOnline()
  try {
    connected = (await Network.getStatus()).connected
  } catch {
    /* keep the current value */
  }
  if (!connected) connected = await probeApiReachable()
  onlineManager.setOnline(connected)
  return connected
}

let recoveryTimer: ReturnType<typeof setInterval> | undefined
let recoveryCheckInFlight = false

function startRecovery() {
  if (recoveryTimer) return
  recoveryTimer = setInterval(() => {
    if (recoveryCheckInFlight) return
    recoveryCheckInFlight = true
    void refreshOnlineStatus().finally(() => {
      recoveryCheckInFlight = false
    })
  }, RECOVERY_INTERVAL_MS)
}

function stopRecovery() {
  if (!recoveryTimer) return
  clearInterval(recoveryTimer)
  recoveryTimer = undefined
}

// onlineManager only notifies on actual changes.
onlineManager.subscribe((online) => {
  if (online) stopRecovery()
  else startRecovery()
})

// Returning to the foreground is the moment a stale flag is most visible.
void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
  if (isActive) void refreshOnlineStatus()
})

// Module-level so useSyncExternalStore gets stable references. Wrapped rather
// than passed directly: isOnline() reads a private class field and throws when
// called without `this`.
const subscribeOnline = (listener: () => void) => onlineManager.subscribe(listener)
const getIsOnline = () => onlineManager.isOnline()

export function useIsOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, getIsOnline, getIsOnline)
}
